import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  useCreateUser,
  useUpdateUser,
  useUser,
  useUserActivityLog,
  useUsers,
} from '../../../hooks/queries';
import { UserStatus } from '../../../services';
import {
  UserFormInitialValues,
  UserInitialValues,
} from '../../../types/UserFormInitialValue';
import { toaster } from '../../ui/toaster';
import type { StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';
import { transformUsers } from '../../../utils/userTransform';

export interface UserRow extends UserFormInitialValues {
  id: string;
  name: string;
  email: string;
  role: string;
  status: StatusType;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  vendor_display?: string;
}

export function useUsersContentData() {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('id');
  const urlTab = searchParams.get('subtab');
  const isUsersTab = !urlTab || urlTab === 'users';
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState<UserRow>();
  const [statusFilter, setStatusFilter] = useState<UserStatus>('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [actionMode, setActionMode] = useState<'add' | 'edit' | ''>('');
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const mappedColumn = sortColumn === 'status' ? 'is_active' : sortColumn;

  const effectiveStatus = filterValues.status || statusFilter;

  const { data, isLoading, error } = useUsers({
    // The API uses LimitOffsetPagination — offset is a row offset,
    // not a page index. Passing `currentPage - 1` advanced one row per page,
    // leaving most users unreachable from the pager.
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    ordering: mappedColumn
      ? `${sortDirection === 'desc' ? '-' : ''}${mappedColumn}`
      : undefined,
    search: searchValue || undefined,
    name: filterValues.name || undefined,
    email: filterValues.email || undefined,
    ...(effectiveStatus !== 'all' && {
      is_active: effectiveStatus === 'active',
    }),
  });
  // Render the server page directly. The old useUsersTable layer
  // re-sliced the already-paginated response against its own never-updated
  // internal page state, truncating any page size above its default of 8.
  const users = useMemo(
    () => (data ? transformUsers(data.results) : []),
    [data],
  );
  const { data: userById, isError: userByIdError } = useUser(Number(urlId));
  const isSingleUserView = Boolean(
    urlId && isUsersTab && userById && !userByIdError,
  );
  const userDetails = isSingleUserView
    ? transformUsers([
        userById as unknown as Parameters<typeof transformUsers>[0][0],
      ])
    : users;

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  // server-side filtering only — kept for the consumer contract
  const filteredUsers = userDetails;

  const totalItems = isSingleUserView
    ? userDetails.length
    : (data?.count ?? 0);

  const hasActiveFilters = useMemo(() => {
    return (
      Boolean(searchValue) ||
      effectiveStatus !== 'all' ||
      Object.values(filterValues).some(Boolean)
    );
  }, [searchValue, effectiveStatus, filterValues]);

  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const [actionDialog, setActionDialog] = useState({
    action: '',
    open: false,
  });
  const { data: userActivityDetail } = useUserActivityLog(
    Number(currentRow?.id),
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter, pageSize, filterValues]);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setActionMode('');
    setCurrentRow(undefined);
  };

  const userFormDefaultValues = useMemo(
    () => ({
      ...UserInitialValues,
      first_name: actionMode === 'edit' ? currentRow?.first_name || '' : '',
      last_name: actionMode === 'edit' ? currentRow?.last_name || '' : '',
      id: actionMode === 'edit' ? currentRow?.id || '' : '',
      email: actionMode === 'edit' ? currentRow?.email || '' : '',
    }),
    [actionMode, currentRow],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = async (values: any) => {
    try {
      if (actionMode === 'edit') {
        const { password, confirm_password, ...updateValues } = values;
        await updateMutation.mutateAsync({
          id: Number(currentRow?.id),
          data: updateValues,
        });
        toaster.create({
          title: 'User has been updated',
          type: 'success',
        });
      } else {
        await createMutation.mutateAsync(values);
        toaster.create({
          title: 'User has been created',
          type: 'success',
        });
      }
      setDrawerOpen(false);
    } catch {
      toaster.create({
        title:
          actionMode === 'edit'
            ? 'Failed to update user'
            : 'Failed to create user',
        type: 'error',
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleResetPassword = async (values: any) => {
    try {
      await updateMutation.mutateAsync({
        id: Number(currentRow?.id),
        data: values,
      });
      toaster.create({
        title: 'Password has been updated',
        type: 'success',
      });
      setActionDialog({
        action: '',
        open: false,
      });
    } catch {
      toaster.create({
        title: 'Failed to update password',
        type: 'error',
      });
    }
  };

  const openEditDrawer = useCallback((row: UserRow) => {
    setActionMode('edit');
    setCurrentRow(row);
    setDrawerOpen(true);
  }, []);

  const openAddDrawer = useCallback(() => {
    setActionMode('add');
    setCurrentRow(undefined);
    setDrawerOpen(true);
  }, []);

  return {
    // State
    drawerOpen,
    bulkInviteOpen,
    setBulkInviteOpen,
    currentRow,
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    actionMode,
    sortColumn,
    sortDirection,
    filterValues,
    actionDialog,
    setActionDialog,
    drawerContentRef,

    // Data
    users,
    filteredUsers,
    hasActiveFilters,
    totalItems,
    totalPages,
    isLoading,
    error,
    userActivityDetail,
    userFormDefaultValues,

    // Handlers
    handleSort,
    handleFilterChange,
    handleFilterClear,
    handleDrawerClose,
    handleSubmit,
    handleResetPassword,
    openEditDrawer,
    openAddDrawer,
  };
}
